import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Actions, ofActionDispatched, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GetAccountStatusAction } from '../actions/account.actions';
import {
  CancelReplaceBalanceAction,
  DeclareNoBalanceYetAction,
  LoadCurrentBalanceAction,
  ReplaceBalanceAction,
  ResetFinancingRequestFormAction,
  SetBalancePeriodAction,
  UploadBalanceAction
} from '../actions/financing-request-form.actions';
import { FinancingApiService } from '../api/financing-api.service';
import { JobPollingService, PENDING_JOB_TIMEOUT } from '../api/job-polling.service';
import { AccountStatus, AccountUserType } from '../models/account-status.model';
import { IDeclaredSale } from '../models/declared-sales.model';
import { CurrentBalanceStatus, IBalance, ICurrentBalance, IDeclaredSales } from '../models/financing-negotiations.model';
import { BalanceStatus, FinancingRequestBalanceModel, IBalanceAnalysisResult } from '../models/financing-request-balance.model';
import { BalanceAnalysisInterpreter } from '../util/balance-analysis.interpreter';
import { BalancePeriodUtils } from '../util/balance-period.utils';
import { DeclaredSalesUtils } from '../util/declared-sales.utils';
import { Constants } from '../util/constants';
import { AccountState } from './account.state';

export interface FinancingRequestFormStateModel {
  balance: FinancingRequestBalanceModel;
  /** Histórico de ventas declaradas de la empresa (null = todavía no cargado). */
  salesHistory: IDeclaredSale[] | null;
}

export const BALANCE_MESSAGES = {
  /** CA11: fallback cuando el dictamen RECHAZADO no trae `justificacion`. */
  ANALYSIS_REJECTED: 'El archivo cargado no parece ser un balance válido. Verificá el documento e intentá nuevamente.',
  MANUAL_PENDING: 'Estamos revisando tu balance. Podés continuar con tu solicitud. Confirmá la fecha de cierre.',
  EXPIRED: 'Tu último balance está fuera de vigencia. Cargá un balance más reciente.',
  REJECTED: 'Tu último balance fue rechazado. Cargá uno nuevo.',
  UPLOAD_ERROR: 'No pudimos procesar el archivo. Intentá nuevamente.',
  ANALYSIS_FAILED: 'No pudimos analizar el documento. Verificá que sea un PDF legible (no escaneado como imagen) e intentá nuevamente.',
  MISSING_COMPANY_DATA: 'No pudimos obtener los datos de tu empresa. Recargá la página e intentá nuevamente.'
};

/** Estados en los que el balance sirve para la solicitud (y habilita el bloque de ventas). */
const USABLE_STATUSES: BalanceStatus[] = ['EXISTING', 'DETECTED', 'MANUAL_PENDING'];

const initialBalance: FinancingRequestBalanceModel = {
  balance: null,
  balanceValidity: null,
  storedClosingPeriod: null,
  status: 'LOADING',
  previousStatus: null,
  uploadProgress: 0,
  uploadedFileName: null,
  closingPeriod: null,
  detectedPeriod: null,
  periodModifiedByUser: false,
  periodEstimated: false,
  periodSaveStatus: 'IDLE',
  analysisResult: null,
  message: null,
  justification: null
};

const initialState: FinancingRequestFormStateModel = {
  balance: initialBalance,
  salesHistory: null
};

@State<FinancingRequestFormStateModel>({
  name: new StateToken<FinancingRequestFormStateModel>('financingRequestForm'),
  defaults: initialState
})
@Injectable()
export class FinancingRequestFormState {

  constructor(
    private store: Store,
    private actions$: Actions,
    private financingApi: FinancingApiService,
    private jobPolling: JobPollingService
  ) {}

  // ---------------------------------------------------------------- selectors

  @Selector()
  static getBalanceBlock(state: FinancingRequestFormStateModel): FinancingRequestBalanceModel {
    return state?.balance;
  }

  @Selector()
  static getDeclaredSalesHistory(state: FinancingRequestFormStateModel): IDeclaredSale[] | null {
    return state?.salesHistory;
  }

  /** El bloque de balance aplica únicamente a personas jurídicas. */
  @Selector([AccountState.getUserType])
  static requiresBalance(userType: AccountUserType): boolean {
    return userType?.id?.toUpperCase() === 'JURIDICA';
  }

  /**
   * Período de cierre utilizable por el bloque de ventas (CA20):
   *  - Persona jurídica: el cierre del balance vigente; null si todavía no hay un balance válido.
   *  - Persona física (no presenta balance): se declaran los últimos N meses cerrados.
   *
   * Nota: con `injectContainerState: false` (app.module) un selector con dependencias NO recibe el
   * estado del contenedor, por eso el balance se toma de `getBalanceBlock`.
   */
  @Selector([FinancingRequestFormState.requiresBalance, FinancingRequestFormState.getBalanceBlock])
  static getUsableClosingPeriod(requiresBalance: boolean, balance: FinancingRequestBalanceModel): string | null {
    if (!requiresBalance) {
      return BalancePeriodUtils.closingForLastMonths(Constants.SALES_MONTHS_WITHOUT_BALANCE);
    }
    if (!balance || !USABLE_STATUSES.includes(balance.status)) return null;
    return BalancePeriodUtils.isValid(balance.closingPeriod) ? balance.closingPeriod : null;
  }

  /** CA35 (parcial): el bloque de balance no impide el envío. */
  @Selector([FinancingRequestFormState.requiresBalance, FinancingRequestFormState.getBalanceBlock, FinancingRequestFormState.getUsableClosingPeriod])
  static isBalanceBlockValid(requiresBalance: boolean, balance: FinancingRequestBalanceModel, usablePeriod: string | null): boolean {
    if (!requiresBalance) return true;
    if (balance?.status === 'NO_BALANCE_YET') return true;
    // CA10: la fecha de cierre corregida tiene que quedar guardada antes de enviar.
    if (balance?.periodSaveStatus === 'SAVING' || balance?.periodSaveStatus === 'ERROR') return false;
    return !!usablePeriod;
  }

  // ---------------------------------------------------------------- actions

  /**
   * CA5b / CA21: `current-balance` devuelve `{ currentFile, declaracionesVentas }`:
   * el balance vigente y el histórico de ventas declaradas (para la precarga).
   */
  @Action(LoadCurrentBalanceAction)
  handleLoadCurrentBalance(ctx: StateContext<FinancingRequestFormStateModel>) {
    this.patchBalance(ctx, { ...initialBalance, status: 'LOADING' });

    return this.financingApi.getCurrentBalance().pipe(
      catchError(err => {
        // Sin balance (404/204) o error del servicio: se ofrece la carga, nunca se bloquea el form.
        console.warn('No se pudo obtener el balance vigente', err);
        return of(null as ICurrentBalance | null);
      }),
      tap(response => {
        console.log('response', response)
        const validity = response?.currentFile?.status ?? null;
        const storedClosingPeriod = BalancePeriodUtils.normalize(response?.currentFile?.closingDate ?? null);
        this.patchBalance(ctx, {
          ...this.evaluateStoredBalance(response?.currentFile?.file ?? null, validity, storedClosingPeriod),
          balanceValidity: validity,
          storedClosingPeriod
        });
        ctx.patchState({ salesHistory: FinancingRequestFormState.toDeclaredSales(response?.declaracionesVentas) });
      })
    );
  }

  @Action(UploadBalanceAction, { cancelUncompleted: true })
  handleUploadBalance(ctx: StateContext<FinancingRequestFormStateModel>, action: UploadBalanceAction) {
    const file = action.payload.file;
    const current = ctx.getState().balance;

    this.patchBalance(ctx, {
      status: 'UPLOADING',
      uploadProgress: 0,
      uploadedFileName: file?.name ?? null,
      analysisResult: null,
      message: null,
      justification: null,
      periodSaveStatus: 'IDLE',
      // El reemplazo mantiene el balance anterior hasta tener uno nuevo válido.
      previousStatus: current.previousStatus
    });

    const cancel$ = this.actions$.pipe(
      ofActionDispatched(ReplaceBalanceAction, CancelReplaceBalanceAction, ResetFinancingRequestFormAction, DeclareNoBalanceYetAction)
    );

    return this.getAccountStatus(ctx).pipe(
      map(accountStatus => this.buildUploadBody(file, accountStatus)),
      switchMap(body => this.financingApi.uploadNewBalance(body)),
      tap((event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.patchBalance(ctx, { uploadProgress: Math.round(100 * event.loaded / event.total) });
        }
      }),
      filter((event: HttpEvent<any>): event is HttpResponse<any> => event.type === HttpEventType.Response),
      tap(() => this.patchBalance(ctx, { status: 'ANALYZING', uploadProgress: 100 })),
      switchMap(response => {
        const jobId = response.body?.jobId;
        // CA8: polling en segundo plano, sin loader global, para que el usuario siga completando el form.
        return jobId
          ? this.jobPolling.pollJob<IBalanceAnalysisResult>(jobId, {
              showLoader: false,
              isPending: body => BalanceAnalysisInterpreter.isPending(body)
            })
          : of(response.body as IBalanceAnalysisResult);
      }),
      switchMap(result => this.applyAnalysisResult(ctx, result)),
      catchError(err => {
        if (err?.error?.error === PENDING_JOB_TIMEOUT) {
          // CA13: vence la ventana → no se bloquea; el usuario informa el período.
          this.setManualPending(ctx, null, null, null);
        } else {
          console.error('Error cargando balance', err);
          this.patchBalance(ctx, {
            status: 'ERROR',
            message: err?.message === BALANCE_MESSAGES.MISSING_COMPANY_DATA ? err.message : BALANCE_MESSAGES.UPLOAD_ERROR
          });
        }
        return of(null);
      }),
      takeUntil(cancel$)
    );
  }

  /**
   * CA10 / CA12 / CA13: el usuario confirma o corrige la fecha de cierre.
   * Se aplica al instante en el front y se guarda en backend
   * (`PUT current-balance/closing-date` con el `id` del análisis y el último día del mes).
   * Cambios seguidos (mes y año) se juntan en un solo guardado; uno nuevo cancela el anterior.
   */
  @Action(SetBalancePeriodAction, { cancelUncompleted: true })
  handleSetBalancePeriod(ctx: StateContext<FinancingRequestFormStateModel>, action: SetBalancePeriodAction) {
    const period = BalancePeriodUtils.normalize(action.payload.period);
    if (!period) return;
    const balance = ctx.getState().balance;
    const analysisId = BalanceAnalysisInterpreter.unwrap(balance.analysisResult)?.id ?? null;
    const closingDate = BalancePeriodUtils.toClosingDate(period);
    const canSave = !!analysisId && !!closingDate && BalancePeriodUtils.isValid(period);

    this.patchBalance(ctx, {
      closingPeriod: period,
      periodModifiedByUser: period !== balance.detectedPeriod,
      // Sin id de análisis (balance cargado en otra sesión) no hay dónde guardarlo todavía.
      periodSaveStatus: !analysisId ? 'NOT_SAVABLE' : (canSave ? 'SAVING' : 'IDLE')
    });
    if (!canSave) return;

    const isCurrent = () => ctx.getState().balance.closingPeriod === period;
    const cancel$ = this.actions$.pipe(
      ofActionDispatched(ReplaceBalanceAction, CancelReplaceBalanceAction, ResetFinancingRequestFormAction, DeclareNoBalanceYetAction, UploadBalanceAction)
    );

    return timer(Constants.BALANCE_CLOSING_DATE_SAVE_DEBOUNCE_MS).pipe(
      switchMap(() => this.financingApi.updateBalanceClosingDate({ id: analysisId, closingDate })),
      tap(() => { if (isCurrent()) this.patchBalance(ctx, { periodSaveStatus: 'SAVED' }); }),
      catchError(err => {
        console.error('No se pudo guardar la fecha de cierre', err);
        if (isCurrent()) this.patchBalance(ctx, { periodSaveStatus: 'ERROR' });
        return of(null);
      }),
      takeUntil(cancel$)
    );
  }

  @Action(ReplaceBalanceAction)
  handleReplaceBalance(ctx: StateContext<FinancingRequestFormStateModel>) {
    const balance = ctx.getState().balance;
    const canGoBack = balance.status === 'EXISTING' ? 'EXISTING' : balance.previousStatus;
    this.patchBalance(ctx, {
      status: 'NONE',
      previousStatus: canGoBack,
      uploadProgress: 0,
      uploadedFileName: null,
      analysisResult: null,
      message: null,
      justification: null,
      periodSaveStatus: 'IDLE'
    });
  }

  @Action(CancelReplaceBalanceAction)
  handleCancelReplace(ctx: StateContext<FinancingRequestFormStateModel>) {
    const balance = ctx.getState().balance;
    this.patchBalance(ctx, { ...this.evaluateStoredBalance(balance.balance, balance.balanceValidity, balance.storedClosingPeriod), previousStatus: null });
  }

  @Action(DeclareNoBalanceYetAction)
  handleDeclareNoBalanceYet(ctx: StateContext<FinancingRequestFormStateModel>, action: DeclareNoBalanceYetAction) {
    const balance = ctx.getState().balance;
    if (action.payload.noBalanceYet) {
      this.patchBalance(ctx, {
        status: 'NO_BALANCE_YET',
        previousStatus: balance.status === 'NO_BALANCE_YET' ? balance.previousStatus : balance.status,
        closingPeriod: null,
        message: null
      });
    } else {
      this.patchBalance(ctx, { ...this.evaluateStoredBalance(balance.balance, balance.balanceValidity, balance.storedClosingPeriod), previousStatus: null });
    }
  }

  @Action(ResetFinancingRequestFormAction)
  handleReset(ctx: StateContext<FinancingRequestFormStateModel>) {
    ctx.setState(initialState);
  }

  // ---------------------------------------------------------------- helpers

  /** Normaliza `declaracionesVentas` (período como array de Java o string) → 'YYYY-MM'. */
  static toDeclaredSales(list: IDeclaredSales[] | null | undefined): IDeclaredSale[] {
    return DeclaredSalesUtils.fromBackend(list);
  }

  private patchBalance(ctx: StateContext<FinancingRequestFormStateModel>, patch: Partial<FinancingRequestBalanceModel>) {
    ctx.patchState({ balance: { ...ctx.getState().balance, ...patch } });
  }

  /**
   * Traduce el balance guardado de la empresa al estado del bloque (CA5b / CA15).
   * `vigencia` es `currentFile.status` de current-balance: ACTIVE | EXPIRED | NO_BALANCE.
   * Cuando viene, manda backend; si no viene, se evalúa la vigencia en el front (24 meses).
   * `closingPeriod` es `currentFile.closingDate` ya normalizado a 'YYYY-MM'.
   */
  private evaluateStoredBalance(
    balance: IBalance | null,
    vigencia: CurrentBalanceStatus | string | null = null,
    closingPeriod: string | null = null
  ): Partial<FinancingRequestBalanceModel> {
    const base: Partial<FinancingRequestBalanceModel> = {
      balance,
      uploadProgress: 0,
      uploadedFileName: null,
      analysisResult: null,
      periodModifiedByUser: false,
      periodEstimated: false,
      periodSaveStatus: 'IDLE',
      message: null,
      justification: null
    };
    const status = (vigencia ?? '').toUpperCase();

    if (status === 'NO_BALANCE' || !balance?.id) {
      return { ...base, status: 'NONE', closingPeriod: null, detectedPeriod: null };
    }

    if (this.isRejectedStatus(balance.status)) {
      return { ...base, status: 'NONE', closingPeriod: null, detectedPeriod: null, message: BALANCE_MESSAGES.REJECTED };
    }

    // Si current-balance devuelve el cierre se usa; si no (hoy), PROVISORIO: createdDate − 6 meses.
    // Cierre: currentFile.closingDate → file.fechaCierre → PROVISORIO: createdDate − 6 meses.
    const storedPeriod = closingPeriod ?? BalancePeriodUtils.normalize(balance.fechaCierre ?? null);
    const period = storedPeriod ?? BalancePeriodUtils.estimateClosingPeriod(balance.createdDate);
    const periodEstimated = !storedPeriod;
    const expired = status ? status === 'EXPIRED' : BalancePeriodUtils.isExpired(period);

    if (expired) {
      return { ...base, status: 'EXPIRED', closingPeriod: period, detectedPeriod: period, periodEstimated, message: BALANCE_MESSAGES.EXPIRED };
    }

    return { ...base, status: 'EXISTING', closingPeriod: period, detectedPeriod: period, periodEstimated };
  }

  /** CA9 / CA11 / CA12 / CA15 a partir del resultado del job. */
  private applyAnalysisResult(ctx: StateContext<FinancingRequestFormStateModel>, result: IBalanceAnalysisResult | null): Observable<any> {
    const analysis = BalanceAnalysisInterpreter.interpret(result);

    switch (analysis.kind) {
      case 'FAILED':
        // Error técnico del job (p. ej. `java.lang.NullPointerException: text`): no se muestra al usuario.
        console.error('Falló el análisis del balance', result?.errorMessage ?? result);
        this.patchBalance(ctx, {
          status: 'ERROR',
          analysisResult: result,
          closingPeriod: null,
          detectedPeriod: null,
          message: BALANCE_MESSAGES.ANALYSIS_FAILED,
          justification: null
        });
        return of(null);

      case 'REJECTED':
        // CA11: dictamen RECHAZADO → se muestra la justificación y se pide otro archivo.
        this.patchBalance(ctx, {
          status: 'REJECTED',
          analysisResult: result,
          closingPeriod: null,
          detectedPeriod: null,
          message: analysis.justification ? null : BALANCE_MESSAGES.ANALYSIS_REJECTED,
          justification: analysis.justification
        });
        return of(null);

      case 'MANUAL_REVIEW':
      case 'NO_PERIOD':
      case 'LOW_CONFIDENCE':
        // CA12: dictamen REQUIERE REVISIÓN MANUAL (o sin período / baja confianza): la PyME confirma
        // el cierre y continúa; el documento queda para validación manual del analista.
        this.setManualPending(ctx, result, analysis.period, analysis.justification);
        return of(null);

      case 'DETECTED': {
        const period = analysis.period;
        if (BalancePeriodUtils.isExpired(period)) {
          // CA15: fuera de vigencia → mensaje explícito, se pide otro archivo.
          this.patchBalance(ctx, {
            status: 'EXPIRED', analysisResult: result, closingPeriod: period, detectedPeriod: period,
            periodEstimated: false, message: BALANCE_MESSAGES.EXPIRED, justification: analysis.justification
          });
          return of(null);
        }

        this.patchBalance(ctx, {
          status: 'DETECTED',
          analysisResult: result,
          closingPeriod: period,
          detectedPeriod: period,
          periodModifiedByUser: false,
          periodEstimated: false,
          previousStatus: null,
          message: null,
          justification: analysis.justification
        });

        // Refresca el balance guardado (id / fecha de carga) y el histórico, sin alterar el estado del bloque.
        return this.financingApi.getCurrentBalance().pipe(
          tap(response => {
            if (response?.currentFile?.file?.id) {
              this.patchBalance(ctx, {
                balance: response.currentFile.file,
                balanceValidity: response.currentFile.status ?? null,
                storedClosingPeriod: BalancePeriodUtils.normalize(response.currentFile.closingDate ?? null)
              });
            }
            if (response?.declaracionesVentas) {
              ctx.patchState({ salesHistory: FinancingRequestFormState.toDeclaredSales(response.declaracionesVentas) });
            }
          }),
          catchError(() => of(null))
        );
      }
    }
  }

  /**
   * CA12 / CA13: se prellena con el período detectado (si lo hay) o, PROVISORIO, con
   * fecha de carga − 6 meses; el usuario lo confirma o corrige.
   */
  private setManualPending(
    ctx: StateContext<FinancingRequestFormStateModel>,
    result: IBalanceAnalysisResult | null,
    detectedPeriod: string | null,
    justification: string | null = null
  ) {
    const period = detectedPeriod ?? BalancePeriodUtils.estimateClosingPeriod(result?.createdAt ?? null);
    this.patchBalance(ctx, {
      status: 'MANUAL_PENDING',
      analysisResult: result,
      closingPeriod: period,
      detectedPeriod: period,
      periodModifiedByUser: false,
      periodEstimated: !detectedPeriod,
      previousStatus: null,
      message: BALANCE_MESSAGES.MANUAL_PENDING,
      justification
    });
  }

  private isRejectedStatus(status: string | null | undefined): boolean {
    const s = (status ?? '').toLowerCase();
    return s.includes('reject') || s.includes('rechaz');
  }

  private getAccountStatus(ctx: StateContext<FinancingRequestFormStateModel>): Observable<AccountStatus> {
    const snapshot = this.store.selectSnapshot(AccountState.getAccountStatus);
    if (snapshot) return of(snapshot);
    return ctx.dispatch(new GetAccountStatusAction()).pipe(
      take(1),
      map(() => this.store.selectSnapshot(AccountState.getAccountStatus))
    );
  }

  /**
   * form-data esperado por `/analysis/async`.
   * `userCompanyId` se omite por ahora (a definir con backend: idealmente inferido del token).
   */
  private buildUploadBody(file: File, accountStatus: AccountStatus | null): FormData {
    const razonSocial = accountStatus?.companyData?.name;
    const cuit = accountStatus?.companyData?.idNumber;
    const userId = accountStatus?.verificationStatus?.id;
    if (!razonSocial || !cuit || userId == null) {
      throw new Error(BALANCE_MESSAGES.MISSING_COMPANY_DATA);
    }

    const body = new FormData();
    body.append('file', file, file.name);
    body.append('razonSocial', razonSocial);
    body.append('cuit', cuit);
    body.append('docTypeHint', 'BALANCE');
    body.append('userId', String(userId));
    return body;
  }
}
